import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Zap, Database, ChevronRight } from 'lucide-react';
const tabs = [{
  id: 'query',
  label: 'Query',
  icon: Search
}, {
  id: 'skills',
  label: 'Skills',
  icon: Zap
}, {
  id: 'sources',
  label: 'Sources',
  icon: Database
}];
export function Showcase() {
  const [activeTab, setActiveTab] = useState('query');
  return <section className="py-32 relative overflow-hidden" data-id="element-186">
      <div className="max-w-6xl mx-auto px-6" data-id="element-187">
        <div className="mb-16 md:flex items-end justify-between" data-id="element-188">
          <div className="max-w-2xl" data-id="element-189">
            <h2 className="font-serif text-4xl md:text-5xl mb-4 text-cozy-text" data-id="element-190">
              A workspace that thinks with you.
            </h2>
            <p className="text-cozy-muted text-lg" data-id="element-191">
              Interact with your memory graph through a beautiful,
              keyboard-first interface designed for speed.
            </p>
          </div>

          <div className="mt-8 md:mt-0 flex p-1 bg-cozy-border/30 rounded-lg border border-cozy-border w-fit" data-id="element-192">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2 transition-colors ${activeTab === tab.id ? 'text-cozy-text' : 'text-cozy-muted hover:text-cozy-text'}`} data-id="element-193">
                {activeTab === tab.id && <motion.div layoutId="activeTab" className="absolute inset-0 bg-white rounded-md shadow-sm border border-cozy-border/50" transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-194" />}
                <span className="relative z-10 flex items-center gap-2" data-id="element-195">
                  <tab.icon className="w-4 h-4" data-id="element-196" />
                  {tab.label}
                </span>
              </button>)}
          </div>
        </div>

        <div className="relative rounded-2xl border border-cozy-border bg-white shadow-2xl shadow-black/5 overflow-hidden aspect-[16/10] md:aspect-[16/9]" data-id="element-197">
          {/* Browser Chrome */}
          <div className="h-12 border-b border-cozy-border bg-cozy-bg/50 flex items-center px-4 gap-4" data-id="element-198">
            <div className="flex gap-1.5" data-id="element-199">
              <div className="w-3 h-3 rounded-full bg-cozy-border" data-id="element-200" />
              <div className="w-3 h-3 rounded-full bg-cozy-border" data-id="element-201" />
              <div className="w-3 h-3 rounded-full bg-cozy-border" data-id="element-202" />
            </div>
            <div className="flex-1 max-w-md mx-auto bg-white border border-cozy-border rounded-md h-7 flex items-center justify-center text-xs text-cozy-muted font-mono" data-id="element-203">
              app.getcozy.dev
            </div>
          </div>

          {/* Content Area */}
          <div className="relative h-[calc(100%-3rem)] bg-cozy-bg/20" data-id="element-204">
            <AnimatePresence mode="wait" data-id="element-205">
              {activeTab === 'query' && <motion.div key="query" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="absolute inset-0 p-8 flex flex-col" data-id="element-206">
                  <div className="flex-1 space-y-6 overflow-y-auto hide-scrollbar" data-id="element-207">
                    <div className="flex gap-4" data-id="element-208">
                      <div className="w-8 h-8 rounded-full bg-cozy-accent/10 flex items-center justify-center shrink-0" data-id="element-209">
                        <span className="text-xs font-medium text-cozy-accent" data-id="element-210">
                          You
                        </span>
                      </div>
                      <div className="bg-white border border-cozy-border rounded-2xl rounded-tl-none p-4 text-sm max-w-xl shadow-sm" data-id="element-211">
                        Summarize the feedback from the beta testers on the new
                        dashboard.
                      </div>
                    </div>
                    <div className="flex gap-4" data-id="element-212">
                      <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center shrink-0" data-id="element-213">
                        <SparklesIcon className="w-4 h-4 text-white" data-id="element-214" />
                      </div>
                      <div className="bg-white border border-cozy-border rounded-2xl rounded-tl-none p-4 text-sm max-w-2xl shadow-sm space-y-3" data-id="element-215">
                        <p data-id="element-216">
                          I found 14 pieces of feedback across Slack and Zendesk
                          regarding the new dashboard. Here's the summary:
                        </p>
                        <ul className="list-disc pl-4 space-y-1 text-cozy-muted" data-id="element-217">
                          <li data-id="element-218">
                            <strong className="text-cozy-text" data-id="element-219">
                              Performance:
                            </strong>{' '}
                            8 users noted it loads significantly faster.
                          </li>
                          <li data-id="element-220">
                            <strong className="text-cozy-text" data-id="element-221">
                              Navigation:
                            </strong>{' '}
                            4 users struggled to find the export button.
                          </li>
                          <li data-id="element-222">
                            <strong className="text-cozy-text" data-id="element-223">Bugs:</strong> 2
                            users reported the date picker resetting on timezone
                            change.
                          </li>
                        </ul>
                        <div className="pt-2 flex gap-2" data-id="element-224">
                          <span className="text-xs px-2 py-1 bg-cozy-bg rounded border border-cozy-border text-cozy-muted" data-id="element-225">
                            Sources: #beta-feedback, Zendesk tags
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 relative" data-id="element-226">
                    <input type="text" placeholder="Ask anything about your work..." className="w-full bg-white border border-cozy-border rounded-xl px-4 py-3 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-cozy-accent/20 shadow-sm" readOnly data-id="element-227" />
                    <Search className="w-4 h-4 text-cozy-muted absolute left-4 top-3.5" data-id="element-228" />
                  </div>
                </motion.div>}

              {activeTab === 'skills' && <motion.div key="skills" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="absolute inset-0 p-8" data-id="element-229">
                  <div className="grid grid-cols-2 gap-4 h-full" data-id="element-230">
                    <div className="bg-white border border-cozy-border rounded-xl p-5 shadow-sm flex flex-col" data-id="element-231">
                      <div className="flex justify-between items-start mb-4" data-id="element-232">
                        <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center" data-id="element-233">
                          <Zap className="w-5 h-5 text-orange-600" data-id="element-234" />
                        </div>
                        <span className="text-xs font-medium px-2 py-1 bg-green-100 text-green-700 rounded-full" data-id="element-235">
                          Active
                        </span>
                      </div>
                      <h4 className="font-medium mb-1" data-id="element-236">Weekly Status Digest</h4>
                      <p className="text-xs text-cozy-muted mb-4 flex-1" data-id="element-237">
                        Compiles updates from GitHub PRs and Jira tickets into a
                        Friday Slack message.
                      </p>
                      <button className="text-xs font-medium text-cozy-accent flex items-center gap-1 hover:gap-2 transition-all" data-id="element-238">
                        Edit Skill <ChevronRight className="w-3 h-3" data-id="element-239" />
                      </button>
                    </div>
                    <div className="bg-white border border-cozy-border rounded-xl p-5 shadow-sm flex flex-col" data-id="element-240">
                      <div className="flex justify-between items-start mb-4" data-id="element-241">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center" data-id="element-242">
                          <Zap className="w-5 h-5 text-blue-600" data-id="element-243" />
                        </div>
                        <span className="text-xs font-medium px-2 py-1 bg-gray-100 text-gray-600 rounded-full" data-id="element-244">
                          Paused
                        </span>
                      </div>
                      <h4 className="font-medium mb-1" data-id="element-245">
                        Triage Customer Tickets
                      </h4>
                      <p className="text-xs text-cozy-muted mb-4 flex-1" data-id="element-246">
                        Reads incoming Intercom messages and categorizes them by
                        urgency and product area.
                      </p>
                      <button className="text-xs font-medium text-cozy-accent flex items-center gap-1 hover:gap-2 transition-all" data-id="element-247">
                        Edit Skill <ChevronRight className="w-3 h-3" data-id="element-248" />
                      </button>
                    </div>
                  </div>
                </motion.div>}

              {activeTab === 'sources' && <motion.div key="sources" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="absolute inset-0 p-8" data-id="element-249">
                  <div className="bg-white border border-cozy-border rounded-xl shadow-sm overflow-hidden" data-id="element-250">
                    <div className="px-4 py-3 border-b border-cozy-border bg-cozy-bg/50 flex justify-between items-center" data-id="element-251">
                      <span className="text-sm font-medium" data-id="element-252">
                        Connected Sources
                      </span>
                      <button className="text-xs bg-white border border-cozy-border px-2 py-1 rounded shadow-sm" data-id="element-253">
                        Add Source
                      </button>
                    </div>
                    <div className="divide-y divide-cozy-border" data-id="element-254">
                      {['Slack Workspace', 'Google Drive', 'Notion', 'GitHub'].map((source, i) => <div key={i} className="px-4 py-3 flex items-center justify-between hover:bg-cozy-bg/50 transition-colors" data-id="element-255">
                          <div className="flex items-center gap-3" data-id="element-256">
                            <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center" data-id="element-257">
                              <Database className="w-4 h-4 text-gray-500" data-id="element-258" />
                            </div>
                            <div data-id="element-259">
                              <div className="text-sm font-medium" data-id="element-260">
                                {source}
                              </div>
                              <div className="text-xs text-cozy-muted" data-id="element-261">
                                Synced 2 mins ago
                              </div>
                            </div>
                          </div>
                          <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded border border-green-100" data-id="element-262">
                            Healthy
                          </span>
                        </div>)}
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}
function SparklesIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-263">
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" data-id="element-264" />
      <path d="M5 3v4" data-id="element-265" />
      <path d="M19 17v4" data-id="element-266" />
      <path d="M3 5h4" data-id="element-267" />
      <path d="M17 19h4" data-id="element-268" />
    </svg>;
}